// Peak planning arithmetic — shared by the Peak Planning page and testable in Node.
//
// Pure functions, no React and no fetch: give it the daily actuals and a plan and it
// gives back the forecast and the plan-vs-actual review. Kept out of the page so the
// arithmetic can be checked on its own (transform with @babel/core preset-env and
// require it, the same way RouteSectionsEngine's exported functions are checked).
//
// Definitions used throughout (say them out loud, because AP's form does not):
//   • A SEASON is named by its year and runs 1 Nov → 31 Dec.
//   • "Daily volume" on the AP form is a WEEKDAY rate. Saturdays run at roughly
//     half a weekday, so averaging them into the same number understates the
//     weekday rate and makes a good forecast look wrong. Weekday and Saturday are
//     kept apart here and only added back together at the monthly total.
//   • An OPERATING DAY is a weekday that is not an NSW public holiday, plus any
//     weekend day the plan says we run.
import { NSW_PUBLIC_HOLIDAYS } from './leaveRules';

const DAY_MS = 86400000;
const dUTC = (iso) => new Date(`${iso}T00:00:00Z`);
const isoOf = (d) => d.toISOString().slice(0, 10);
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const round = (v, dp = 0) => {
  const f = 10 ** dp;
  return Math.round((Number(v) || 0) * f) / f;
};
// Percentage difference of `actual` against `plan`, positive = actual came in above.
export const variance = (actual, plan) => (plan ? (actual / plan - 1) * 100 : null);

// Every day in a month, tagged. `kind` is 'weekday' | 'saturday' | 'sunday', and a
// weekday that is a gazetted NSW public holiday is 'holiday' — the depot does not
// deliver on one, so it must not be counted as capacity.
export function monthDays(year, monthIdx) {
  const out = [];
  const last = new Date(Date.UTC(year, monthIdx + 1, 0)).getUTCDate();
  for (let day = 1; day <= last; day++) {
    const d = new Date(Date.UTC(year, monthIdx, day));
    const iso = isoOf(d);
    const dow = d.getUTCDay();
    let kind = 'weekday';
    if (dow === 6) kind = 'saturday';
    else if (dow === 0) kind = 'sunday';
    else if (NSW_PUBLIC_HOLIDAYS.has(iso)) kind = 'holiday';
    out.push({ iso, dow, kind });
  }
  return out;
}

export const weekdayCount = (year, monthIdx) => monthDays(year, monthIdx).filter((d) => d.kind === 'weekday').length;
export const saturdays = (year, monthIdx) => monthDays(year, monthIdx).filter((d) => d.kind === 'saturday').map((d) => d.iso);
export const sundays = (year, monthIdx) => monthDays(year, monthIdx).filter((d) => d.kind === 'sunday').map((d) => d.iso);

// What one month actually did. `daily` is { iso: [parcels, drivers] } from
// wahr_history — days with no row simply did not run (a shutdown day or a Sunday).
export function monthActual(daily, year, monthIdx) {
  const out = {
    year, monthIdx, label: `${MONTHS[monthIdx]} ${year}`,
    total: 0, days: 0, driverDays: 0,
    weekday: { total: 0, days: 0, avg: 0 },
    saturday: { total: 0, days: 0, avg: 0 },
    sunday: { total: 0, days: 0, avg: 0 },
    avgDrivers: 0, articlesPerDriver: 0,
    peak: null, has: false,
  };
  for (const d of monthDays(year, monthIdx)) {
    const row = daily[d.iso];
    if (!row || !row[0]) continue;
    const [parcels, drivers] = row;
    out.has = true;
    out.total += parcels;
    out.days += 1;
    out.driverDays += drivers;
    const bucket = d.dow === 6 ? out.saturday : d.dow === 0 ? out.sunday : out.weekday;
    bucket.total += parcels;
    bucket.days += 1;
    if (!out.peak || parcels > out.peak.parcels) {
      out.peak = { iso: d.iso, parcels, drivers, perDriver: drivers ? parcels / drivers : 0 };
    }
  }
  for (const b of [out.weekday, out.saturday, out.sunday]) b.avg = b.days ? b.total / b.days : 0;
  out.avgDrivers = out.days ? out.driverDays / out.days : 0;
  out.articlesPerDriver = out.driverDays ? out.total / out.driverDays : 0;
  return out;
}

// Monday-anchored weeks across the season, so the shape of the ramp is visible
// (peak week, the cliff after the last delivery day before Christmas).
export function seasonWeeks(daily, year) {
  const weeks = new Map();
  for (const iso of Object.keys(daily)) {
    if (iso < `${year}-11-01` || iso > `${year}-12-31`) continue;
    const d = dUTC(iso);
    const monday = isoOf(new Date(d.getTime() - ((d.getUTCDay() + 6) % 7) * DAY_MS));
    const w = weeks.get(monday) || { wc: monday, total: 0, days: 0, drivers: 0 };
    w.total += daily[iso][0];
    w.days += 1;
    w.drivers += daily[iso][1];
    weeks.set(monday, w);
  }
  return [...weeks.values()].sort((a, b) => (a.wc < b.wc ? -1 : 1));
}

// The whole Nov + Dec season, plus the ten biggest days (the days that decide how
// many drivers you actually needed, as opposed to the monthly average).
export function seasonActual(daily, year) {
  const nov = monthActual(daily, year, 10);
  const dec = monthActual(daily, year, 11);
  const days = [];
  for (const iso of Object.keys(daily)) {
    if (iso >= `${year}-11-01` && iso <= `${year}-12-31` && daily[iso][0]) {
      const [parcels, drivers] = daily[iso];
      days.push({ iso, parcels, drivers, perDriver: drivers ? parcels / drivers : 0 });
    }
  }
  days.sort((a, b) => (a.iso < b.iso ? -1 : 1));
  const total = nov.total + dec.total;
  const busiest = [...days].sort((a, b) => b.parcels - a.parcels).slice(0, 10);
  return {
    year, nov, dec, total, days,
    operatingDays: days.length,
    busiest,
    weeks: seasonWeeks(daily, year),
    has: nov.has || dec.has,
  };
}

// The June base the AP form starts from: the base month's weekday rate, the logins
// that carried it, and the articles per driver that implies.
export function baseActual(daily, baseMonth) {
  const [y, m] = (baseMonth || '').split('-').map(Number);
  if (!y || !m) return null;
  const a = monthActual(daily, y, m - 1);
  if (!a.has) return null;
  return {
    month: baseMonth, label: a.label,
    daily: a.weekday.avg,
    drivers: a.avgDrivers,
    articlesPerDriver: a.articlesPerDriver,
    total: a.total, days: a.days,
  };
}

// How many weekend days the plan says we run in a month, and the drivers on them.
export function plannedWeekend(plan, year, monthIdx) {
  const wk = (plan && plan.weekend) || {};
  const isos = [...saturdays(year, monthIdx), ...sundays(year, monthIdx)];
  const on = isos.filter((iso) => wk[iso] && wk[iso].operating);
  return {
    days: on.length,
    drivers: on.reduce((s, iso) => s + (Number(wk[iso].drivers) || 0), 0) / (on.length || 1),
    isos: on,
  };
}

// ── the forecast ─────────────────────────────────────────────────────────────
// Two bases, deliberately both computed so the meeting can see them next to each
// other rather than arguing about one number:
//
//   'actual' — last season's ACTUAL weekday rate × (1 + growth%). This is what
//              "peak will be 8% higher than last year" literally means.
//   'ap'     — this June's base rate × AP's own uplift factors (Nov 1.198,
//              Dec 1.450, recovered from the 2025 agreement). This is the model AP
//              used, and it only grows if the BAU base grows.
//
// Monthly volume = weekday rate × operating weekdays + Saturday rate × planned
// Saturdays, where the Saturday rate carries last season's Saturday-to-weekday
// relationship forward. Drivers = daily volume ÷ articles per driver, which is
// exactly how the AP form derives its "Daily Drivers (logins)".
function forecastMonth({ year, monthIdx, weekdayRate, satRatio, plan }) {
  const wkDays = weekdayCount(year, monthIdx);
  const wknd = plannedWeekend(plan, year, monthIdx);
  const satRate = weekdayRate * satRatio;
  const monthly = weekdayRate * wkDays + satRate * wknd.days;
  const app = Number(plan.articles_per_driver) || 1;
  return {
    label: `${MONTHS[monthIdx]} ${year}`,
    weekdayDays: wkDays,
    weekendDays: wknd.days,
    weekendISOs: wknd.isos,
    daily: weekdayRate,
    saturdayDaily: satRate,
    monthly,
    driversNeeded: weekdayRate / app,
    operatingDays: wkDays + wknd.days,
  };
}

export function buildForecast(plan, daily, apUplift) {
  const year = Number(plan.year);
  const base = baseActual(daily, plan.base_month);
  const last = seasonActual(daily, year - 1);
  const growth = (Number(plan.growth_pct) || 0) / 100;
  const uplift = apUplift || { nov: 1.19818, dec: 1.4495 };

  // Saturday runs at a fraction of a weekday; carry last season's own ratio
  // forward rather than inventing one. Falls back to a half-day if we have none.
  const satRatio = (m) => (m && m.weekday.avg && m.saturday.days ? m.saturday.avg / m.weekday.avg : 0.5);

  const rates = {
    actual: { nov: last.nov.weekday.avg * (1 + growth), dec: last.dec.weekday.avg * (1 + growth) },
    ap: { nov: (base ? base.daily : 0) * uplift.nov, dec: (base ? base.daily : 0) * uplift.dec },
  };
  const basis = plan.basis === 'ap' ? 'ap' : 'actual';

  const build = (which) => {
    const nov = forecastMonth({ year, monthIdx: 10, weekdayRate: rates[which].nov, satRatio: satRatio(last.nov), plan });
    const dec = forecastMonth({ year, monthIdx: 11, weekdayRate: rates[which].dec, satRatio: satRatio(last.dec), plan });
    return { nov, dec, total: nov.monthly + dec.monthly };
  };

  const chosen = build(basis);
  const other = build(basis === 'ap' ? 'actual' : 'ap');

  // Capacity, stated plainly: drivers × articles per driver × operating days.
  // (AP's workbook computes a capacity figure it never shows the formula for; this
  // one you can check on the back of an envelope in the meeting.)
  const app = Number(plan.articles_per_driver) || 1;
  const totalDrivers = (Number(plan.bau_drivers) || 0) + (Number(plan.bau_xl) || 0)
    + (Number(plan.extra_drivers) || 0) + (Number(plan.extra_xl) || 0);
  const capacityOf = (m) => totalDrivers * app * m.operatingDays;

  // The peak DAY is what breaks a plan, not the monthly average: last season's
  // busiest day, grown by the same factor as the month it sat in.
  const lastPeakDay = last.busiest[0] || null;
  const peakFactor = basis === 'actual'
    ? 1 + growth
    : (last.dec.weekday.avg ? rates.ap.dec / last.dec.weekday.avg : 1);
  const peakDay = lastPeakDay ? {
    parcels: lastPeakDay.parcels * peakFactor,
    // Two driver numbers, because they answer different questions. The planning
    // rate is what the AP form is built on; the demonstrated rate is what the
    // drivers actually carried on last season's biggest day (325 articles, not
    // 261). Plan the vans on the first, argue the second.
    driversNeeded: (lastPeakDay.parcels * peakFactor) / app,
    driversAtLastRate: lastPeakDay.perDriver ? (lastPeakDay.parcels * peakFactor) / lastPeakDay.perDriver : null,
    was: lastPeakDay,
  } : null;

  // The headline the meeting actually argues about: "peak will be N% up on last
  // year" is a claim about the SEASON TOTAL. The built plan only reaches it if the
  // weekend days are ticked, so show the target and the gap rather than letting an
  // untouched weekend grid quietly shrink the forecast.
  const target = basis === 'actual' ? last.total * (1 + growth) : null;

  return {
    year, base, last, basis, rates, uplift, target,
    targetGap: target === null ? null : chosen.total - target,
    nov: chosen.nov, dec: chosen.dec, total: chosen.total,
    alternate: { basis: basis === 'ap' ? 'actual' : 'ap', ...other },
    drivers: {
      bau: (Number(plan.bau_drivers) || 0) + (Number(plan.bau_xl) || 0),
      extra: (Number(plan.extra_drivers) || 0) + (Number(plan.extra_xl) || 0),
      total: totalDrivers,
      novNeeded: chosen.nov.driversNeeded,
      decNeeded: chosen.dec.driversNeeded,
      peakNeeded: peakDay ? peakDay.driversNeeded : null,
      shortfallNov: chosen.nov.driversNeeded - totalDrivers,
      shortfallDec: chosen.dec.driversNeeded - totalDrivers,
      shortfallPeak: peakDay ? peakDay.driversNeeded - totalDrivers : null,
    },
    capacity: {
      nov: capacityOf(chosen.nov),
      dec: capacityOf(chosen.dec),
      novVariance: capacityOf(chosen.nov) - chosen.nov.monthly,
      decVariance: capacityOf(chosen.dec) - chosen.dec.monthly,
    },
    peakDay,
    growthVsLast: last.total ? ((chosen.total / last.total) - 1) * 100 : null,
  };
}

// ── the review ───────────────────────────────────────────────────────────────
// What the season promised versus what it delivered. `plan.agreed` holds the
// figures exactly as they left the signed workbook, so this measures the promise,
// not a recomputation of it.
export function reviewSeason(plan, daily) {
  const year = Number(plan.year);
  const actual = seasonActual(daily, year);
  const prev = seasonActual(daily, year - 1);
  const a = (plan && plan.agreed) || null;
  const base = baseActual(daily, plan.base_month);

  const rows = [];
  if (a && actual.has) {
    rows.push(
      { key: 'nov_monthly', label: 'November volume', plan: a.nov_monthly, actual: actual.nov.total, unit: 'parcels' },
      { key: 'dec_monthly', label: 'December volume', plan: a.dec_monthly, actual: actual.dec.total, unit: 'parcels' },
      { key: 'season', label: 'Peak season total', plan: a.nov_monthly + a.dec_monthly, actual: actual.total, unit: 'parcels' },
      { key: 'nov_daily', label: 'November daily rate (weekdays)', plan: a.nov_daily, actual: actual.nov.weekday.avg, unit: 'parcels/day' },
      { key: 'dec_daily', label: 'December daily rate (weekdays)', plan: a.dec_daily, actual: actual.dec.weekday.avg, unit: 'parcels/day' },
      { key: 'nov_drivers', label: 'November drivers on the road', plan: a.nov_total_drivers, actual: actual.nov.avgDrivers, unit: 'logins' },
      { key: 'dec_drivers', label: 'December drivers on the road', plan: a.dec_total_drivers, actual: actual.dec.avgDrivers, unit: 'logins' },
      // The planning rate is a CAPACITY number — what a driver can carry on the
      // heaviest day — so the season average sitting well under it is the plan
      // working, not the plan failing. Kept on the page because the gap between
      // the two is exactly the headroom the season ran on.
      { key: 'app', label: 'Articles per driver (season average vs planning rate)', plan: plan.articles_per_driver, actual: actual.total / (actual.nov.driverDays + actual.dec.driverDays || 1), unit: 'articles', informational: true },
    );
  }
  for (const r of rows) r.variance = variance(r.actual, r.plan);

  // The base-data check: did AP start from the same June we did? If this is inside
  // a percent, every other variance on the page is a real variance.
  const baseCheck = (a && base) ? {
    plan: a.base_daily, actual: base.daily, variance: variance(base.daily, a.base_daily),
  } : null;

  return {
    year, actual, prev, rows, baseCheck,
    plannedGrowth: a ? Number(plan.growth_pct) : null,
    realisedGrowth: prev.has && actual.has ? ((actual.total / prev.total) - 1) * 100 : null,
    seasonVariance: (a && actual.has) ? variance(actual.total, a.nov_monthly + a.dec_monthly) : null,
  };
}

